"use client";

import type { ChangeEvent } from "react";
import React, { useState, useEffect, useRef, useCallback } from "react";
import { FilledButton } from "~/components/buttons/filled-button";
import type { Country } from "~/components/dropdown/country-dropdown";
import CountryDropdown from "~/components/dropdown/country-dropdown";
import { allCountries } from "country-telephone-data";
import type { CountryData } from "~/utils/types";
import { toast } from "react-toastify";
import { InlineAlert } from "~/components/inline-alert/inline-alert";
import Image from "next/image";
import { Phone, Check, Clock, RotateCw, ShieldCheck, Loader2 } from "lucide-react";
import { api } from "~/utils/api";
import { validatePhoneNumber } from "~/utils/validatePhoneNumber";

const EXCLUDED_COUNTRIES = ["by", "cu", "kp", "sy", "ve", "ir"];
const countryList: Country[] = allCountries
    .filter(({ iso2 }: CountryData) => !EXCLUDED_COUNTRIES.includes(iso2))
    .map(({ name, iso2, dialCode }: CountryData) => ({
        name, iso2, dialCode,
        flag: `https://flagcdn.com/w40/${iso2.toLowerCase()}.png`,
    }));
const DEFAULT_COUNTRY = countryList.find((c) => c.iso2 === "us") ?? countryList[0]!;

function OtpInput({ value, onChange, disabled, hasError }: { value: string; onChange: (v: string) => void; disabled?: boolean; hasError?: boolean }) {
    const inputs = useRef<(HTMLInputElement | null)[]>([]);
    const handleChange = (i: number, e: ChangeEvent<HTMLInputElement>) => {
        const digit = e.target.value.replace(/\D/g, "").slice(-1);
        const arr = (value + "      ").slice(0, 6).split("");
        arr[i] = digit || "";
        onChange(arr.join("").replace(/\s/g, "").slice(0, 6));
        if (digit && i < 5) inputs.current[i + 1]?.focus();
    };
    const handleKeyDown = (i: number, e: React.KeyboardEvent) => {
        if (e.key === "Backspace" && !value[i] && i > 0) inputs.current[i - 1]?.focus();
    };
    const handlePaste = (e: React.ClipboardEvent) => {
        e.preventDefault();
        const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
        onChange(pasted);
        inputs.current[Math.min(pasted.length, 5)]?.focus();
    };
    return (
        <div className="flex gap-1.5 sm:gap-2">
            {Array.from({ length: 6 }).map((_, i) => (
                <input
                    key={i}
                    ref={(el) => { inputs.current[i] = el; }}
                    type="text" inputMode="numeric" maxLength={1}
                    value={value[i] || ""}
                    onChange={(e) => handleChange(i, e)}
                    onKeyDown={(e) => handleKeyDown(i, e)}
                    onPaste={i === 0 ? handlePaste : undefined}
                    disabled={disabled}
                    className={`w-9 h-11 sm:w-11 sm:h-13 text-center text-base sm:text-lg font-semibold rounded-lg border-2 bg-white outline-none transition-all duration-200 text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed ${value[i]
                        ? "border-[var(--primary-color)] shadow-[0_0_0_3px_var(--primary-color-light,rgba(var(--primary-rgb,34,197,94),0.1))]"
                        : hasError
                            ? "border-[#FF0000]"
                            : "border-gray-200 hover:border-gray-300 focus:border-[var(--primary-color)] focus:shadow-[0_0_0_3px_var(--primary-color-light,rgba(var(--primary-rgb,34,197,94),0.1))]"
                        }`}
                />
            ))}
        </div>
    );
}

function useCountdown(initial: number) {
    const [seconds, setSeconds] = useState(0);
    const [running, setRunning] = useState(false);
    useEffect(() => {
        if (!running || seconds <= 0) return;
        const id = setInterval(() => setSeconds((s) => s - 1), 1000);
        return () => clearInterval(id);
    }, [running, seconds]);
    const start = useCallback(() => { setSeconds(initial); setRunning(true); }, [initial]);
    const mm = String(Math.floor(seconds / 60)).padStart(2, "0");
    const ss = String(seconds % 60).padStart(2, "0");
    return { seconds, display: `${mm}:${ss}`, start };
}

interface VerifyPhoneProps {
    onVerifiedChange?: (isVerified: boolean) => void;
    // Outer spacing — defaults to the full-bleed inset used on the Waitlist/NameYourPrice
    // pages. Callers embedding this inside an already-padded container (e.g. a checkout
    // form column) should pass their own spacing (or "") instead of stacking margins.
    className?: string;
}

export default function VerifyPhone({ onVerifiedChange, className = "mx-4 md:mx-8" }: VerifyPhoneProps) {
    const [phoneStep, setPhoneStep] = useState<"idle" | "codeSent" | "verified">("idle");
    const [selectedCountry, setSelectedCountry] = useState<Country>(DEFAULT_COUNTRY);
    const [phoneNumber, setPhoneNumber] = useState("");
    const [phoneError, setPhoneError] = useState("");
    const [verifiedNumber, setVerifiedNumber] = useState<{ phone: string; country: Country } | null>(null);
    const [otp, setOtp] = useState("");
    const [otpError, setOtpError] = useState("");
    const [remainingResends, setRemainingResends] = useState(2);
    const timer = useCountdown(120); // 2 minutes countdown for resending code

    const { data: userCountry } = api.user.getCountryCode.useQuery({});
    const sendCodeMutation = api.user.sendPhoneVerificationCode.useMutation();
    const verifyCodeMutation = api.user.verifyPhoneCode.useMutation();
    const resendCodeMutation = api.user.resendPhoneVerificationCode.useMutation();
    const { data: userInfo, isLoading: isLoadingUserInfo } = api.user.getAdditionalInfoForUser.useQuery({
        refetchOnWindowFocus: false,
    });

    const hasSynced = useRef(false);
    // Sync user's existing phone data on load
    useEffect(() => {
        if (!userInfo || hasSynced.current) return;
        hasSynced.current = true;
        if (userInfo.phoneNumber) setPhoneNumber(userInfo.phoneNumber);
        // Find country: prefer iso2, fall back to dialCode for old records
        const matchedCountry = userInfo.phoneNumberCountryIso2
            ? countryList.find((c) => c.iso2 === userInfo.phoneNumberCountryIso2)
            : countryList.find((c) => +c.dialCode === userInfo.phoneNumberCountryCode);
        if (matchedCountry) setSelectedCountry(matchedCountry);
        if (userInfo.phoneNumberVerified && userInfo.phoneNumber) {
            setPhoneStep("verified");
            setVerifiedNumber({ phone: userInfo.phoneNumber, country: matchedCountry ?? DEFAULT_COUNTRY });
        }
    }, [userInfo]);

    const hasSyncedIpCountry = useRef(false);
    // Fallback: if user has no stored country, detect from IP
    useEffect(() => {
        if (!userCountry?.country || !userInfo || hasSyncedIpCountry.current) return;
        if (userInfo.phoneNumberCountryIso2 ?? userInfo.phoneNumberCountryCode) return;
        hasSyncedIpCountry.current = true;
        const ipCountry = countryList.find((c) => c.iso2 === userCountry.country!.toLowerCase());
        if (ipCountry) setSelectedCountry(ipCountry);
    }, [userCountry, userInfo]);

    const isUnsubscribed = userInfo && !userInfo.isPhoneNotificationsEnabled;
    useEffect(() => {
        if (isUnsubscribed) {
            onVerifiedChange?.(true);
        } else {
            onVerifiedChange?.(phoneStep === "verified");
        }
    }, [phoneStep, isUnsubscribed]);

    // If user unsubscribed mid-flow, reset OTP state
    useEffect(() => {
        if (isUnsubscribed && phoneStep === "codeSent") {
            setPhoneStep("idle");
            setOtp("");
            setOtpError("");
            setPhoneError("");
            if (verifiedNumber) {
                setPhoneNumber(verifiedNumber.phone);
                setSelectedCountry(verifiedNumber.country);
            } else if (userInfo?.phoneNumber) {
                setPhoneNumber(userInfo.phoneNumber);
            }
        }
    }, [isUnsubscribed]);

    const handleSelectCountry = (country: Country) => setSelectedCountry(country);

    const handleSendCode = async () => {
        const validation = validatePhoneNumber(phoneNumber, +selectedCountry.dialCode);
        if (validation !== true) { setPhoneError(validation); return; }
        setPhoneError("");
        try {
            const result = await sendCodeMutation.mutateAsync({
                phoneNumber,
                phoneNumberCountryCode: +selectedCountry.dialCode,
                phoneNumberCountryIso2: selectedCountry.iso2,
                baseURL: window.location.origin,
                platform: navigator.userAgent,
            });
            if (!result.success) {
                setPhoneError(result.message);
                return;
            }
            setPhoneStep("codeSent");
            setRemainingResends(result.remainingResends ?? 2);
            timer.start();
        } catch (err) {
            setPhoneError("Something went wrong. Please try again.");
        }
    };

    const handleVerifyOtp = async () => {
        setOtpError("");
        if (otp.length < 6) { setOtpError("Please enter the full 6-digit code"); return; }
        try {
            const result = await verifyCodeMutation.mutateAsync({ otp });
            if (!result.success) {
                setOtpError(result.message);
                // If locked out, go back to idle after a moment
                if (result.message.includes("Too many failed")) {
                    setTimeout(() => { setPhoneStep("idle"); setOtp(""); setOtpError(""); }, 2000);
                }
                return;
            }
            setPhoneStep("verified");
            setOtpError("");
            setVerifiedNumber({ phone: phoneNumber, country: selectedCountry });
            toast.success("Phone number verified!", {
                progressStyle: { background: "var(--primary-color)" },
            });
        } catch (err) {
            setOtpError("Something went wrong. Please try again.");
        }
    };

    const handleResendCode = async () => {
        setOtp("");
        setOtpError("");
        try {
            const result = await resendCodeMutation.mutateAsync({ platform: navigator.userAgent, baseURL: window.location.origin });
            if (!result.success) {
                setOtpError(result.message);
                if (result.remainingResends !== undefined && result.remainingResends <= 0) {
                    setPhoneStep("idle");
                    setOtp("");
                }
                return;
            }
            setRemainingResends(result.remainingResends ?? 0);
            timer.start();
            toast.success("New code sent!", { progressStyle: { background: "var(--primary-color)" } });
        } catch (err) {
            setOtpError("Failed to resend code. Please try again.");
        }
    };

    const handleChangeNumber = () => {
        setPhoneStep("idle");
        setOtp("");
        setOtpError("");
        setPhoneError("");
    };

    const handleCancelChange = () => {
        if (verifiedNumber) {
            setPhoneNumber(verifiedNumber.phone);
            setSelectedCountry(verifiedNumber.country);
            setPhoneStep("verified");
            setPhoneError("");
        }
    };

    // Check if current number matches the already-verified number
    const isCurrentNumberVerified =
        verifiedNumber !== null &&
        phoneNumber === verifiedNumber.phone &&
        selectedCountry.iso2 === verifiedNumber.country.iso2;

    // Live validation on input change
    const handlePhoneChange = (e: ChangeEvent<HTMLInputElement>) => {
        const digits = e.target.value.replace(/\D/g, "").slice(0, 15);
        setPhoneNumber(digits);
        if (digits.length >= 4) {
            const validation = validatePhoneNumber(digits, +selectedCountry.dialCode);
            setPhoneError(validation === true ? "" : validation);
        } else {
            setPhoneError("");
        }
    };

    if (isLoadingUserInfo) {
        return (
            <div className={`${className} rounded-xl border border-gray-200 bg-white px-4 py-4 sm:px-5 sm:py-5`}>
                <div className="flex items-center gap-3 animate-pulse">
                    <div className="w-8 h-8 rounded-lg bg-gray-200" />
                    <div className="flex-1">
                        <div className="h-4 bg-gray-200 rounded w-32 mb-2" />
                        <div className="h-3 bg-gray-200 rounded w-48" />
                    </div>
                </div>
            </div>
        );
    }

    if (!userInfo?.isPhoneNotificationsEnabled && userInfo?.phoneNumberVerified) return null;

    if (phoneStep === "verified") {
        return (
            <div className={`${className} rounded-xl border border-gray-200 bg-white`}>
                <div className="px-4 py-3 sm:px-5 sm:py-4 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                        <div className="flex-shrink-0 flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 rounded-full"
                            style={{ backgroundColor: "var(--primary-color)" }}>
                            <ShieldCheck size={20} color="white" strokeWidth={2.5} />
                        </div>
                        <div className="min-w-0">
                            <p className="text-[0.8125rem] sm:text-[0.875rem] font-semibold text-gray-800">Phone Number Verified</p>
                            <p className="text-[0.6875rem] sm:text-[0.75rem] text-gray-500 flex items-center gap-1.5 truncate">
                                <Image src={`https://flagcdn.com/w40/${selectedCountry.iso2.toLowerCase()}.png`}
                                    alt={selectedCountry.iso2} className="inline-block rounded-sm flex-shrink-0" width={18} height={12} />
                                <span className="truncate">+{selectedCountry.dialCode} {phoneNumber}</span>
                            </p>
                        </div>
                    </div>
                    <button type="button" onClick={handleChangeNumber}
                        className="flex-shrink-0 text-[0.75rem] sm:text-[0.8125rem] font-medium px-3 py-1.5 rounded-lg transition-colors hover:bg-gray-50"
                        style={{ color: "var(--primary-color)" }}>
                        Change
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className={className}>
            {isUnsubscribed && (
                <div className="flex items-center gap-2 px-4 py-2.5 mb-2 rounded-lg bg-amber-50 border border-amber-200 text-amber-700 text-[0.75rem] sm:text-[0.8125rem]">
                    <span>You’ve unsubscribed from SMS. If u want to receive SMS, Re-enable them from your account settings.</span>
                </div>
            )}
            <div className={`rounded-xl border border-gray-200 bg-white ${isUnsubscribed ? "opacity-50 pointer-events-none select-none" : ""}`}>
                <div className="px-4 py-3 sm:px-5 sm:py-3.5 border-b border-gray-100 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2.5">
                        <div className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-lg"
                            style={{ backgroundColor: "color-mix(in srgb, var(--primary-color) 12%, white)" }}>
                            <Phone size={16} strokeWidth={2.5} style={{ color: "var(--primary-color)" }} />
                        </div>
                        <div>
                            <p className="text-[0.8125rem] sm:text-[0.875rem] font-semibold text-gray-800">Verify Your Phone</p>
                            <p className="text-[0.6875rem] sm:text-[0.75rem] text-gray-500">We&apos;ll text a one-time code.</p>
                        </div>
                    </div>
                    {!isUnsubscribed &&
                        <div className="flex-shrink-0 flex items-center gap-1.5 text-[0.6875rem] font-semibold uppercase tracking-wide text-red">
                            <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ backgroundColor: "red" }} />
                            Required
                        </div>
                    }
                </div>
                <div className="px-4 py-3 sm:px-5 sm:py-4 flex flex-col gap-3">
                    <div>
                        <div className="flex items-center justify-between mb-1.5">
                            <label htmlFor="phone" className="text-[0.75rem] sm:text-[0.8125rem] text-gray-500 font-medium">Mobile number</label>
                            {phoneStep === "codeSent" && (
                                <span className="text-[0.6875rem] sm:text-[0.75rem] font-semibold flex items-center gap-1" style={{ color: "var(--primary-color)" }}>
                                    <Check size={14} strokeWidth={2.5} /> Code sent
                                </span>
                            )}
                        </div>
                        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                            <div className={`flex items-center flex-1 h-12 rounded-lg bg-secondary-white px-1 text-[0.875rem] text-gray-500 outline-none border-2 transition-colors duration-200 border-transparent ${phoneStep === "codeSent" ? "opacity-60" : ""}`}>
                                <CountryDropdown key={selectedCountry.iso2} defaultCountry={selectedCountry.iso2} items={countryList} onSelect={handleSelectCountry} />
                                <input type="text" inputMode="numeric" value={phoneNumber}
                                    onChange={handlePhoneChange}
                                    disabled={phoneStep === "codeSent"} placeholder="9988776655"
                                    className="input-phone-number w-full h-full px-2.5 text-[0.875rem] text-gray-800 bg-transparent outline-none disabled:text-gray-500 disabled:cursor-not-allowed" />
                            </div>
                            {phoneStep === "idle" && (
                                <div className="flex items-center gap-2 w-full sm:w-auto">
                                    <FilledButton onClick={handleSendCode}
                                        disabled={sendCodeMutation.isLoading || !phoneNumber || !!phoneError || isCurrentNumberVerified}
                                        className="flex items-center justify-center gap-1 max-w-[12.5rem] w-full py-[.28rem] md:py-1.5 text-[0.625rem] md:text-[0.875rem] disabled:opacity-50 transition-opacity duration-300">
                                        {sendCodeMutation.isLoading
                                            ? <span className="flex items-center justify-center gap-2"><Loader2 size={16} className="animate-spin" />Sending…</span>
                                            : isCurrentNumberVerified ? "Verified" : "Send Code"}
                                    </FilledButton>
                                </div>
                            )}
                        </div>
                        {phoneError && <p className="text-[0.6875rem] sm:text-[0.75rem] text-red mt-1.5">{phoneError}</p>}
                        {verifiedNumber && phoneStep === "idle" && (
                            <p className="text-[0.6875rem] sm:text-[0.85rem] text-gray-400 mt-1">
                                {isCurrentNumberVerified
                                    ? "This number is already verified."
                                    : `Your verified number is +${verifiedNumber.country.dialCode} ${verifiedNumber.phone}.`
                                }{" "}
                                <button type="button" onClick={handleCancelChange} className="font-medium text-primary">
                                    Go back
                                </button>
                            </p>
                        )}
                    </div>
                    {phoneStep === "codeSent" && (
                        <div className="flex flex-col gap-3 pt-1 border-t border-gray-100 mt-1">
                            <div className="flex items-center justify-between pt-3">
                                <label htmlFor="otp" className="text-[0.75rem] sm:text-[0.8125rem] text-gray-500 font-medium">Enter 6-digit code</label>
                                {timer.seconds > 0 && remainingResends > 0 && (
                                    <span className="flex items-center gap-1.5 text-[0.75rem] sm:text-[0.8125rem] font-semibold tabular-nums" style={{ color: "var(--primary-color)" }}>
                                        <Clock size={14} strokeWidth={2} /> {timer.display}
                                    </span>
                                )}
                            </div>
                            <div className="flex flex-col sm:flex-row items-start sm:items-end gap-3">
                                <OtpInput value={otp} onChange={(v) => { setOtp(v); setOtpError(""); }} disabled={verifyCodeMutation.isLoading || resendCodeMutation.isLoading} hasError={!!otpError} />
                                <FilledButton onClick={handleVerifyOtp} disabled={otp.length < 6 || verifyCodeMutation.isLoading}
                                    className="h-11 sm:h-13 !py-0 px-4 text-[0.8125rem] whitespace-nowrap disabled:opacity-40 w-full sm:w-auto">
                                    {verifyCodeMutation.isLoading
                                        ? <span className="flex items-center justify-center gap-2"><Loader2 size={16} className="animate-spin" />Verifying…</span>
                                        : <span className="flex items-center justify-center gap-1.5"><Check size={16} strokeWidth={2.5} />Verify</span>}
                                </FilledButton>
                            </div>
                            {otpError && (
                                <InlineAlert status="error">
                                    {otpError}
                                </InlineAlert>
                            )}
                            <div className="flex items-center justify-between gap-3">
                                <div className="flex items-center justify-center gap-2">
                                    <span className="text-[0.67875rem] sm:text-[0.85rem] text-gray-500 whitespace-nowrap">
                                        Didn&apos;t get it?
                                    </span>
                                    {timer.seconds <= 0 && remainingResends > 0 ? (
                                        <button
                                            type="button"
                                            onClick={handleResendCode}
                                            disabled={resendCodeMutation.isLoading || verifyCodeMutation.isLoading}
                                            className="font-semibold flex items-center gap-1 text-primary text-[0.67875rem] sm:text-[0.85rem] whitespace-nowrap transition-colors hover:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            <RotateCw
                                                size={12}
                                                className={resendCodeMutation.isLoading ? "animate-spin" : ""}
                                            />
                                            Resend code
                                        </button>
                                    ) : remainingResends <= 0 ? (
                                        <span className="text-[0.67875rem] sm:text-[0.85rem] text-gray-300 whitespace-nowrap">Resend limit exceeded</span>
                                    ) : (
                                        <span className="text-[0.67875rem] sm:text-[0.85rem] text-gray-300 whitespace-nowrap">Resend code</span>
                                    )}
                                </div>
                                <button type="button" onClick={handleChangeNumber}
                                    disabled={verifyCodeMutation.isLoading || resendCodeMutation.isLoading}
                                    className="text-[0.6875rem] sm:text-[0.85rem] font-medium text-gray-500 hover:text-gray-600 transition-colors disabled:cursor-not-allowed disabled:text-gray-300">
                                    Change number
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}